import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import EhsConfigService from "../services/EhsConfigService";
import UsersService from "@velocity-ehs/user-utils-module";

export interface ActivateEhsConfigurationInput {
  customerId: string;
  id: string;
}
export interface EhsConfigurationFormInput {
  id: string;
}

export enum EhsConfigurationActions {
  ACTIVATE = "activate",
}

const useEhsConfigurationMutations = () => {
  const activateMutation = useMutation(
    (req: ActivateEhsConfigurationInput) =>
      EhsConfigService.activateEhsConfiguration(req),
    ResponseHandling("customer_application_edit_success")
  );

  const mutate = async (
    action: EhsConfigurationActions,
    input: EhsConfigurationFormInput
  ) => {
    switch (action) {
      case EhsConfigurationActions.ACTIVATE:
        const activate = mapToActivate(input);

        return await activateMutation.mutateAsync(activate);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: activateMutation.isLoading,
    },
  };
};

export default useEhsConfigurationMutations;

function mapToActivate(
  input: EhsConfigurationFormInput
): ActivateEhsConfigurationInput {
  return {
    customerId: UsersService.customer.id,
    id: input.id,
  };
}

function ResponseHandling(messageKey: string) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
      queryClient.invalidateQueries({ queryKey: ["ehsConfigurations"] });
    },
  };
}
